// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useId, useRef, useState } from 'react';

import type { SurveyType } from '../../../api/types';
import { useArmedConfirm } from '../../../app/useArmedConfirm';
import { deleteTypeLabel, partCountText, renamePatch } from '../../../bygning/parts';
import { Button } from '../../Button';
import { Select } from '../../Select';
import { TextInput } from '../../TextInput';
import { FormDialog } from '../../kortlaegning/FormDialog';
import form from '../../kortlaegning/FormDialog.module.css';
import styles from './Inspector.module.css';

export interface EditTypeDialogProps {
  type: SurveyType;
  /** Types the whole type can be merged into; empty hides Flet. */
  mergeTargets: readonly SurveyType[];
  /** Show Slet (not in Gennemgang). */
  canDelete: boolean;
  busy: boolean;
  onCancel: () => void;
  /** The trimmed new name (only sent when it changed and is not blank). */
  onRename: (name: string) => void;
  onMerge: (targetId: number) => void;
  onDelete: () => void;
}

/**
 * "Redigér type" behind the inspector's pencil: Navn (Gem saves it), "Flet
 * hele typen ind i…" and Slet — the last two behind an armed confirm, which
 * acts at once (Gem is only for the name).
 */
export function EditTypeDialog({ type, mergeTargets, canDelete, busy, onCancel, onRename, onMerge, onDelete }: EditTypeDialogProps) {
  const [name, setName] = useState(type.name);
  const nameRef = useRef<HTMLInputElement>(null);
  const [mergeId, setMergeId] = useState<number | null>(null);
  const mergeConfirm = useArmedConfirm<number>(busy, `${type.id}:${mergeId}`);
  const deleteConfirm = useArmedConfirm<number>(busy, type.id);
  const deleteArmed = deleteConfirm.armed === type.id;
  const mergeSelect = useId();
  const mergeLabel = useId();
  const deleteLabel = useId();

  useEffect(() => {
    nameRef.current?.focus();
    nameRef.current?.select();
  }, []);

  function submit() {
    // Ctrl/⌘+Enter and plain Enter reach here even while the button is disabled.
    if (busy) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    if (renamePatch(type, trimmed)) onRename(trimmed);
    onCancel();
  }

  return (
    <FormDialog
      title="Redigér type"
      onCancel={onCancel}
      onSubmit={submit}
      actions={
        <>
          <Button variant="outline" size="sm" onClick={onCancel}>
            Annuller
          </Button>
          <Button type="submit" variant="primary" size="sm" disabled={busy || !name.trim()}>
            Gem
          </Button>
        </>
      }
    >
      <label className={form.field}>
        <span className={form.fieldLabel}>Navn</span>
        <TextInput
          ref={nameRef}
          variant="outline"
          size="sm"
          block
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>

      {mergeTargets.length > 0 && (
        <div className={form.field} role="group" aria-labelledby={mergeLabel}>
          <label id={mergeLabel} htmlFor={mergeSelect} className={form.fieldLabel}>
            Flet hele typen ind i…
          </label>
          <div className={styles.merge}>
            <Select
              variant="outline"
              size="sm"
              block
              id={mergeSelect}
              value={mergeId === null ? '' : String(mergeId)}
              onChange={(e) => setMergeId(e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="">Vælg type…</option>
              {mergeTargets.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
            {mergeId !== null && (
              <Button
                tone="danger"
                size="sm"
                armed={mergeConfirm.armed === mergeId}
                disabled={busy}
                onBlur={mergeConfirm.disarm}
                onClick={(e) => {
                  if (mergeConfirm.armed !== mergeId) {
                    mergeConfirm.arm(mergeId, e.currentTarget);
                    return;
                  }
                  mergeConfirm.disarm();
                  onMerge(mergeId);
                  setMergeId(null);
                }}
              >
                {mergeConfirm.armed === mergeId ? `Bekræft: flet ${partCountText(type.parts.length)}` : 'Flet'}
              </Button>
            )}
          </div>
        </div>
      )}

      {canDelete && (
        <div className={`${form.field} ${styles.danger}`} role="group" aria-labelledby={deleteLabel}>
          <span id={deleteLabel} className={form.fieldLabel}>
            Slet
          </span>
          <div>
            <Button
              tone="danger"
              size="sm"
              armed={deleteArmed}
              disabled={busy}
              onBlur={deleteConfirm.disarm}
              onClick={(e) => {
                if (!deleteArmed) {
                  deleteConfirm.arm(type.id, e.currentTarget);
                  return;
                }
                deleteConfirm.disarm();
                onDelete();
              }}
            >
              {deleteTypeLabel(type, deleteArmed)}
            </Button>
          </div>
        </div>
      )}
    </FormDialog>
  );
}
